// Transactions · filter assembly.
//
// Filter definitions now live on `transactionSchema.<field>.filterable` and are
// lifted via `deriveFilters` (the same pattern every other entity uses). Only
// the handful of filters that DON'T map to a single schema field are declared
// here as an explicit supplement:
//   - `only_cancelled` (checkbox) — status_override eq cancelled.
//   - `request_dpr` — boolean-valued pick (selectFilter only carries string/number).
//   - `missing_checks` — computed across many `<key>_check` columns (covers
//     invoice + document-review steps too).

import React from "react";
import dayjs from "dayjs";
import type { CrudFilter } from "@refinedev/core";
import { Select } from "@ds/forms/Select";
import type { FilterDef } from "@ds/tables/types";
import { EntitySelect } from "@ds/connected/EntitySelect";
import { dateRangeFilter, makeOnSearch, selectFilter } from "@ds/tables/helpers/sharedFilterDefs";
import {
  ChecklistMulti, CancelledCheckbox, InvoicedStatusMulti,
} from "./transactionsFilterCells";

// ─── Reusable simple FilterDefs (queue presets + reports reuse these) ────

export const FILTER_PRODUCT: FilterDef = {
  name: "product_ids", label: "Produkt", type: "custom",
  component: (p) => <EntitySelect kind="product" mode="multiple" maxTagCount="responsive" allowClear placeholder="Produkt" style={{ minWidth: 200 }} {...p} />,
};
export const FILTER_WASTE_CODE: FilterDef = {
  name: "waste_codes", label: "Kod odpadu", type: "custom",
  component: (p) => <EntitySelect kind="wasteCode" allowClear mode="multiple" maxTagCount="responsive" style={{ minWidth: 200 }} {...p} />,
};

/** Quarter → load_date range. Value is "YYYY/QN" string. */
export const FILTER_QUARTER: FilterDef = {
  name: "quarter", label: "Kwartał", type: "custom",
  component: (p) => {
    const now = new Date(); const curY = now.getFullYear();
    const opts: { value: string; label: string }[] = [];
    for (let y = curY; y >= 2026; y--) {
      const maxQ = y === curY ? Math.ceil((now.getMonth() + 1) / 3) : 4;
      for (let q = maxQ; q >= 1; q--) opts.push({ value: `${y}/Q${q}`, label: `${y}/Q${q}` });
    }
    return <Select allowClear placeholder="Kwartał" minWidth={110} options={opts} {...p} />;
  },
};

export const PIPELINE_QUICK_FILTERS: FilterDef[] = [
  { name: "product_id", label: "transactions.fields.product", type: "custom", quick: true,
    component: (p) => <EntitySelect kind="product" mode="multiple" maxTagCount="responsive" allowClear placeholder="Produkt" style={{ minWidth: 200 }} {...p} /> },
  { name: "load_place_company_id", label: "transactions.fields.load_place_company", type: "custom", quick: true,
    component: (p) => <EntitySelect kind="company" mode="multiple" maxTagCount="responsive" allowClear placeholder="Miejsce załadunku" style={{ minWidth: 200 }} {...p} /> },
  { name: "unload_place_company_id", label: "transactions.fields.unload_place_company", type: "custom", quick: true,
    component: (p) => <EntitySelect kind="company" mode="multiple" maxTagCount="responsive" allowClear placeholder="Miejsce rozładunku" style={{ minWidth: 200 }} {...p} /> },
  dateRangeFilter("unload_date_range", "transactions.fields.unload_date", { quick: true }),
];

export const PIPELINE_ADVANCED_FILTERS: FilterDef[] = [
  // Cancelled lives here now (moved off the quick strip).
  { name: "only_cancelled", label: "Anulowane", type: "custom", component: CancelledCheckbox,
    toFilter: (checked: boolean) =>
      checked ? [{ field: "status_override", operator: "eq", value: "cancelled" }] : [] },
  selectFilter({ name: "request_dpr", label: "transactions.fields.request_dpr", placeholder: "Wniosek DPR...",
    options: [{ value: "true", label: "Tak" }, { value: "false", label: "Nie" }], multiple: true }),
  // Each selected check key X means "X is still pending" → OR over `<X>_check`.
  // Covers invoice + document review steps too — no separate invoiced/docs filters.
  { name: "missing_checks",       label: "Brakujące kroki",   type: "custom", component: ChecklistMulti,
    toFilter: (keys: string[]) => [
      { operator: "or", value: keys.map((k) => ({ field: `${k}_check`, operator: "eq", value: "pending" })) },
    ] },
];

// ─── Logistics pipeline filter manager ───────────────────────────────────
//
// Filter set + handler for the managed logistics-pipeline queue tab. Every
// filter maps to a real, directly-filterable transactions column so the
// drawer actually narrows results (the handler is what makes the form apply —
// without it the drawer renders but never filters). Assignment scoping
// (logistics_employee_id) stays on the segmented control, so it's omitted
// here to avoid two competing controls for the same column.

const companyFilter = (name: string, label: string): FilterDef => ({
  name, label, type: "custom",
  component: (p) => <EntitySelect kind="company" mode="multiple" maxTagCount="responsive" allowClear placeholder={label} style={{ minWidth: 200 }} testId={`filter-${name}`} {...p} />,
});
const employeeFilter = (name: string, label: string): FilterDef => ({
  name, label, type: "custom",
  component: (p) => <EntitySelect kind="employee" mode="multiple" maxTagCount="responsive" allowClear placeholder={label} style={{ minWidth: 180 }} testId={`filter-${name}`} {...p} />,
});

export const LOGISTICS_PIPELINE_QUICK_FILTERS: FilterDef[] = [
  FILTER_PRODUCT,
];

// Every filter here maps to a real, directly-filterable column on
// `transactions_with_checks` so the handler below narrows results. Computed /
// cross-field filters (waste-code→product resolution, "missing steps", DPR
// request) need bespoke server logic that `makeOnSearch` can't express, so they
// stay on the audit list (PIPELINE_ADVANCED_FILTERS), not here.
export const LOGISTICS_PIPELINE_ADVANCED_FILTERS: FilterDef[] = [
  companyFilter("load_place_company_ids",    "Miejsce załadunku"),
  companyFilter("unload_place_company_ids",  "Miejsce rozładunku"),
  companyFilter("load_commercial_company_ids",   "Kontrahent (załadunek)"),
  companyFilter("unload_commercial_company_ids", "Kontrahent (rozładunek)"),
  companyFilter("exporter_company_ids",  "Eksporter"),
  companyFilter("importer_company_ids",  "Importer"),
  employeeFilter("load_sales_employee_ids",   "Handlowiec (załadunek)"),
  employeeFilter("unload_sales_employee_ids", "Handlowiec (rozładunek)"),
  { name: "load_invoiced_statuses",   label: "Status FV (załadunek)",   type: "custom", component: InvoicedStatusMulti },
  { name: "unload_invoiced_statuses", label: "Status FV (rozładunek)",  type: "custom", component: InvoicedStatusMulti },
  dateRangeFilter("load_date_range",   "Data załadunku"),
  dateRangeFilter("unload_date_range", "Data rozładunku"),
  dateRangeFilter("pickup_date_range", "Data odbioru"),
  dateRangeFilter("reload_date_range", "Data przeładunku"),
  dateRangeFilter("created_at_range",  "Data utworzenia"),
];

const toIsoDate = (v: unknown): unknown =>
  dayjs.isDayjs(v) ? v.format("YYYY-MM-DD") : v;

/**
 * "Data załadunku" displays `load_date ?? pickup_date_from`, so the range
 * matches that effective date: `load_date` in range, or no `load_date` and
 * `pickup_date_from` in range.
 */
function loadDateRangeFilters(raw: unknown): CrudFilter[] {
  const [start, end] = Array.isArray(raw) ? raw.map(toIsoDate) : [null, null];
  const bounds = (field: string): CrudFilter[] => [
    ...(start ? [{ field, operator: "gte" as const, value: start }] : []),
    ...(end ? [{ field, operator: "lte" as const, value: end }] : []),
  ];
  if (!start && !end) return [];
  return [{
    operator: "or",
    value: [
      { operator: "and", value: bounds("load_date") },
      { operator: "and", value: [{ field: "load_date", operator: "null", value: true }, ...bounds("pickup_date_from")] },
    ],
  }];
}

/**
 * Form values → CrudFilter[] for the logistics pipeline. Built with the shared
 * `makeOnSearch` so each declared filter applies server-side. Names match the
 * FilterDef `name`s above; fields are real transactions columns.
 */
export const logisticsPipelineFilterHandler = makeOnSearch([
  { name: "product_ids",                  field: "product_id",                 operator: "in" },
  { name: "load_place_company_ids",       field: "load_place_company_id",      operator: "in" },
  { name: "unload_place_company_ids",     field: "unload_place_company_id",    operator: "in" },
  { name: "load_commercial_company_ids",  field: "load_commercial_company_id",   operator: "in" },
  { name: "unload_commercial_company_ids", field: "unload_commercial_company_id", operator: "in" },
  { name: "exporter_company_ids",         field: "exporter_company_id",        operator: "in" },
  { name: "importer_company_ids",         field: "importer_company_id",        operator: "in" },
  { name: "load_sales_employee_ids",      field: "load_sales_employee_id",     operator: "in" },
  { name: "unload_sales_employee_ids",    field: "unload_sales_employee_id",   operator: "in" },
  { name: "load_invoiced_statuses",       field: "load_invoiced_status",       operator: "in" },
  { name: "unload_invoiced_statuses",     field: "unload_invoiced_status",     operator: "in" },
  { name: "load_date_range",              operator: "custom", build: loadDateRangeFilters },
  { name: "unload_date_range",            field: "unload_date",                operator: "date-range" },
  { name: "pickup_date_range",            field: "pickup_date",                operator: "date-range" },
  { name: "reload_date_range",            field: "reload_date",                operator: "date-range" },
  { name: "created_at_range",             field: "created_at",                 operator: "date-range" },
]);
